import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import marketService from '../services/marketService';
import useAuth from '../hooks/useAuth';
import './MarketForm.css';

const MarketForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    age: '',
    city: '',
    phone: '',
    position: '',
    experience: '',
    description: '',
    videos: '',
    photos: []
  });

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ text: '', isError: false });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      alert('Máximo de 5 fotos permitidas');
      return;
    }
    setForm({ ...form, photos: files });
    setPreviewPhotos(files.map(file => URL.createObjectURL(file)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ text: '', isError: false });

    const formData = new FormData();
    formData.append('name', form.name.trim());
    formData.append('age', form.age);
    formData.append('city', form.city.trim());
    formData.append('phone', form.phone.trim());
    formData.append('position', form.position.trim() || 'Não informado');
    formData.append('experience', form.experience.trim());
    formData.append('description', form.description.trim());
    formData.append('videos', form.videos.trim());
    form.photos.forEach(photo => formData.append('photos', photo));
    
    if (profilePhoto) {
      formData.append('profilePhoto', profilePhoto);
    }

    try {
      const { success, error } = await marketService.register(formData);

      if (success) {
        setSubmitMessage({ text: 'Cadastro realizado com sucesso!', isError: false });
        
        // ADICIONE O REDIRECIONAMENTO AQUI
        // Após 1.5 segundos, navega para a lista do mercado.
        setTimeout(() => {
          navigate('/market');
        }, 1500);

        // A limpeza do formulário agora é opcional, já que vamos sair da página.
        // Mas podemos manter para o caso de a navegação falhar.
        setForm({
          name: '',
          age: '',
          city: '',
          phone: '',
          position: '',
          experience: '',
          description: '',
          videos: '',
          photos: []
        });
        setProfilePhoto(null);
        setProfilePhotoPreview(null);
        setPreviewPhotos([]);

      } else {
        setSubmitMessage({ text: error || 'Erro no cadastro', isError: true });
      }
    } catch (err) {
      console.error('Registration error:', err);
      setSubmitMessage({ text: 'Erro ao conectar com o servidor', isError: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="market-form-container">
      <h2>📋 Inscreva-se no Mercado da Bola</h2>

      {submitMessage.text && (
        <div className={`market-message ${submitMessage.isError ? 'error' : 'success'}`}>
          {submitMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="market-form-grid">
          <div>
            <label>Nome completo*</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>Idade*</label>
            <input
              name="age"
              type="number"
              min="14"
              max="60"
              value={form.age}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>Cidade*</label>
            <input
              name="city"
              value={form.city}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label>Telefone*</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div>
          <label>Foto de Perfil</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleProfilePhotoUpload}
          />
          {profilePhotoPreview && (
            <img 
              src={profilePhotoPreview} 
              alt="Perfil Preview" 
              style={{ maxWidth: '120px', marginTop: '10px' }} 
            />
          )}
        </div>

        <div>
          <label>Posição*</label>
          <select
            name="position"
            value={form.position}
            onChange={handleChange}
            required
          >
            <option value="">Selecione sua posição</option>
            <option value="Goleiro">Goleiro</option>
            <option value="Zagueiro">Zagueiro</option>
            <option value="Lateral">Lateral</option>
            <option value="Volante">Volante</option>
            <option value="Meia">Meia</option>
            <option value="Atacante">Atacante</option>
            <option value="Árbitro">Árbitro</option>
            <option value="Narrador">Narrador</option>
          </select>
        </div>

        <div>
          <label>Experiência</label>
          <textarea
            name="experience"
            placeholder="Times que já jogou, campeonatos, etc."
            value={form.experience}
            onChange={handleChange}
            rows="2"
          />
        </div>

        <div>
          <label>Descrição</label>
          <textarea
            name="description"
            placeholder="Conte um pouco sobre seu estilo de jogo, qualidades, etc."
            value={form.description}
            onChange={handleChange}
            rows="3"
          />
        </div>

        <div>
          <label>Fotos (máx. 5)</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
          />
          <div className="market-photo-preview">
            {previewPhotos.map((src, index) => (
              <img
                key={index}
                src={src}
                alt="preview"
              />
            ))}
          </div>
        </div>

        <div>
          <label>Links de vídeos</label>
          <input
            name="videos"
            placeholder="YouTube, Vimeo, etc. (separados por vírgula)"
            value={form.videos}
            onChange={handleChange}
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="market-submit-button"
        >
          {isSubmitting ? 'Enviando...' : 'Enviar Inscrição'}
        </button>
      </form>
    </div>
  );
};

export default MarketForm;